import { ExpandArrow } from '@protector/components';
import { Tag, Tooltip } from 'antd';
import { COLOR_OPTIONS } from 'config/style';
import STTypo from 'core/shared/typo/typo.component';
import { TypeOptions } from 'core/shared/typo/typo.models';
import React, { useEffect, useState } from 'react';
import { getDisclosureToggleProps } from '../../disclosure-toggle.utils';
import styles from './filter-section.module.css';

interface FilterSectionProps {
  name: string;
  children: React.ReactNode;
  onClear?: () => void;
  selectedText?: string;
  defaultExpanded?: boolean;
  dataTestId?: string;
  disabled?: boolean;
  disabledTooltip?: string;
}

export const FilterSection: React.FC<FilterSectionProps> = ({
  name,
  children,
  onClear,
  selectedText,
  defaultExpanded = false,
  dataTestId,
  disabled = false,
  disabledTooltip
}) => {
  const [isExpanded, setIsExpanded] = useState(disabled ? false : defaultExpanded);

  const toggleExpanded = () => {
    if (disabled) return;
    setIsExpanded(prev => !prev);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onClear?.();
  };

  useEffect(() => {
    if (disabled) {
      setIsExpanded(false);
    }
  }, [disabled]);

  return (
    <section className={styles.container} data-testid={dataTestId} data-expanded={isExpanded}>
      <Tooltip title={disabled ? disabledTooltip : ''}>
        <header className={styles.header} {...getDisclosureToggleProps(isExpanded, toggleExpanded, disabled)}>
          <div className={styles.headerLeft}>
            <STTypo
              type={TypeOptions.P1}
              fontFamily='Noto Sans'
              fontWeight={600}
              color={disabled ? COLOR_OPTIONS.NEUTRAL_50 : COLOR_OPTIONS.NEUTRAL_100}>
              {name}
            </STTypo>
            {selectedText && !disabled && (
              <Tag closable={!!onClear} onClose={handleClear} data-testid={`${dataTestId}-clear-button`} color={COLOR_OPTIONS.NEUTRAL_10}>
                <STTypo type={TypeOptions.P2} fontFamily='Noto Sans' fontWeight={600} color={COLOR_OPTIONS.NEUTRAL_60}>
                  {selectedText}
                </STTypo>
              </Tag>
            )}
          </div>
          <ExpandArrow expanded={isExpanded} />
        </header>
      </Tooltip>
      <div className={styles.collapsibleContent}>{children}</div>
    </section>
  );
};

export default FilterSection;
